/**
 * Pure, side-effect-free helpers for the Event Email Delivery card.
 *
 * These functions carry the input-varying logic for the card: default form
 * values, existing-record mapping, recipient selection, timing selection,
 * validation, and payload assembly. They are intentionally free of React and
 * network concerns so they can be unit- and property-tested in isolation
 * (see eventEmailUtils.test.js).
 *
 * The shared enums (NOTIFICATION_TYPES, TIMING_OPTIONS) live in
 * availableVariables.js and are re-exported here for consumers.
 *
 * API contract (event_notification_configurations):
 * Never is UI-only and uses DELETE instead of a create/update payload.
 *   {
 *     notification_type: "email",
 *     user_group_id: <int>,
 *     subject: <string>,
 *     message_body: <string>,
 *     timing: "send_on_triggered" | "send_on_untriggered" | "send_on_both"
 *   }
 */

import { NOTIFICATION_TYPES, TIMING_OPTIONS } from "./availableVariables";

export { NOTIFICATION_TYPES, TIMING_OPTIONS };

// ---------------------------------------------------------------------------
// Value / config helpers
// ---------------------------------------------------------------------------

/**
 * The documented default form values for a brand-new event email
 * configuration: the "Email" notification type, no recipient, empty
 * subject/message, and the "never" timing.
 *
 * A fresh object is returned on each call so callers can safely mutate the
 * result without affecting later invocations.
 *
 * @returns {{
 *   notification_type: string,
 *   user_group_id: string,
 *   subject: string,
 *   message_body: string,
 *   timing: string,
 * }}
 */
export const getDefaultEventEmailValues = () => ({
  notification_type: NOTIFICATION_TYPES.EMAIL,
  user_group_id: "",
  subject: "",
  message_body: "",
  timing: TIMING_OPTIONS.NEVER,
});

/**
 * Maps an existing configuration record to form values, falling back to the
 * documented defaults for every field missing on the record. A null/undefined
 * (or non-object) record yields exactly the defaults.
 *
 * The merge is per-field: any field present on the record overrides its
 * default, and any field absent on the record keeps its default.
 *
 * @param {object|null|undefined} config
 * @returns {object} form values
 */
export const mapConfigToValues = (config) => {
  const defaults = getDefaultEventEmailValues();
  if (!config || typeof config !== "object") return defaults;

  // Existing records without timing retain the original triggered default.
  defaults.timing = TIMING_OPTIONS.TRIGGERED;
  return Object.keys(defaults).reduce((values, key) => {
    values[key] = key in config && config[key] != null ? config[key] : defaults[key];
    return values;
  }, {});
};

// ---------------------------------------------------------------------------
// Timing helpers
// ---------------------------------------------------------------------------

/**
 * Single-selection timing setter: returns exactly the chosen option, so that
 * precisely one timing option is active at any time and no previously selected
 * option remains. Idempotent for the same value.
 *
 * @param {string} _current the currently active timing (unused; single-select)
 * @param {string} choice the newly chosen timing option
 * @returns {string} the chosen option
 */
export const setTiming = (_current, choice) => choice;

/**
 * A timing value is valid when it is one of the TIMING_OPTIONS values
 * (send_on_triggered / send_on_untriggered / send_on_both).
 *
 * @param {string} t
 * @returns {boolean}
 */
export const isValidTiming = (t) => t !== TIMING_OPTIONS.NEVER && Object.values(TIMING_OPTIONS).includes(t);

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

/**
 * A value is blank when it has no non-whitespace characters. Null/undefined
 * are treated as the empty string, so both count as blank.
 *
 * @param {string|null|undefined} s
 * @returns {boolean}
 */
export const isBlank = (s) => (s ?? "").trim().length === 0;

/**
 * Validates event email form values, returning an errors object keyed by
 * field. An empty object means the values are valid (submission permitted); a
 * non-empty object blocks submission.
 *
 * Rules:
 * - `never`: skips field validation while delivery is disabled.
 * - `user_group_id`: a recipient user group is required when active.
 * - `subject`: required when blank; too long when length > 255.
 * - `message_body`: required when blank; too long when length > 5000.
 * - `timing`: must be one of the supported timing options.
 *
 * @param {object} values
 * @returns {object} errors ({} means valid)
 */
export const validateEventEmail = (values) => {
  if (values.timing === TIMING_OPTIONS.NEVER) return {};
  const errors = {};

  if (values.user_group_id === "" || values.user_group_id == null) {
    errors.user_group_id = "user_group_required";
  }

  if (isBlank(values.subject)) {
    errors.subject = "subject_required";
  } else if (values.subject.length > 255) {
    errors.subject = "subject_too_long";
  }

  if (isBlank(values.message_body)) {
    errors.message_body = "message_required";
  } else if (values.message_body && values.message_body.length > 5000) {
    errors.message_body = "message_too_long";
  }

  if (!isValidTiming(values.timing)) {
    errors.timing = "timing_invalid";
  }

  return errors;
};

// ---------------------------------------------------------------------------
// Variable search and payload builder
// ---------------------------------------------------------------------------

/**
 * Filters the available-variable list by a search query using a
 * case-insensitive substring match on each variable's `name`. An empty or
 * whitespace-only query returns the full list unchanged; a query that matches
 * no variable name yields an empty list.
 *
 * @param {Array<{ name: string }>} variables
 * @param {string|null|undefined} query
 * @returns {Array<{ name: string }>} the matching variables
 */
export const filterVariables = (variables, query) => {
  const q = (query ?? "").trim().toLowerCase();
  if (q === "") return variables;
  return variables.filter((v) => v.name.toLowerCase().includes(q));
};

/**
 * Assembles the request payload for creating or updating an event email
 * configuration from form values. The payload defaults the notification type
 * to "email" when none is selected, coerces the recipient id to an integer,
 * stores subject and message verbatim (no template transformation), and
 * validates the timing with a "send_on_triggered" fallback.
 *
 * Matches the API contract:
 *   { notification_type, user_group_id, subject, message_body, timing }
 *
 * @param {object} values form values
 * @returns {{
 *   notification_type: string,
 *   user_group_id: number,
 *   subject: string,
 *   message_body: string,
 *   timing: string,
 * }}
 */
export const buildEventEmailPayload = (values) => ({
  notification_type: values.notification_type || NOTIFICATION_TYPES.EMAIL,
  user_group_id: parseInt(values.user_group_id, 10),
  subject: values.subject ?? "",
  message_body: values.message_body ?? "",
  timing: isValidTiming(values.timing) ? values.timing : TIMING_OPTIONS.TRIGGERED,
});
